import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { assemblyService, attendanceService, questionService } from 'services/api';
import AssemblyReport from 'components/AssemblyReport';
import CuorumPanel from 'components/CuorumPanel';
import ActivarPreguntaModal from 'pages/admin/preguntas/ActivarPreguntaModal';
import { MOTIVO_NO_INICIADA, MOTIVO_FINALIZADA } from 'pages/admin/preguntas/EstadoAsambleaAviso';
import TemporizadorVotacion, { fijarTemporizador, quitarTemporizador } from 'components/TemporizadorVotacion';
import ConfirmarFinalizarModal, { votacionesAbiertas } from 'components/ConfirmarFinalizarModal';
import { useMeeting } from 'context/MeetingContext';
import { useAuth } from 'context/AuthContext';
import styles from './AssemblyAdminDashboard.module.css';
import { ESTADO_ASAMBLEA as STATUS_LABEL } from 'utils/etiquetas';
import useCierreAlerta from 'utils/useCierreAlerta';

const POLL_MS = 8000;

const STATUS_COLOR = { pendiente: '#f79009',    en_progreso: '#12b76a',     finalizada: '#8a92a6' };

// ── Sin asamblea asignada ─────────────────────────────────────────────────────
function NoAssemblyScreen() {
  return (
    <div className={styles.sinAsamblea}>
      <div className={styles.sinAsambleaIcono}>
        <i className={`fas fa-gavel ${styles.sinAsambleaGlifo}`} />
      </div>
      <h2 className={styles.sinAsambleaTitulo}>Sin asamblea asignada</h2>
      <p className={styles.sinAsambleaTexto}>
        El super administrador aún no te ha asignado ninguna asamblea.<br />
        Contacta al administrador para que realice la asignación.
      </p>
    </div>
  );
}

export default function AssemblyAdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { abrirReunion } = useMeeting();
  const [assembly,    setAssembly]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [attendances, setAttendances] = useState([]);
  const [questions,   setQuestions]   = useState([]);
  const [toggling,    setToggling]    = useState(null);
  const [alert,       setAlert]       = useState(null);
  const cerrarAlertaEn = useCierreAlerta(setAlert);
  const [activar,     setActivar]     = useState(null);   // F-17 (b): sí/no por confirmar
  const intervalRef = useRef(null);

  // ── Cargar asamblea asignada ─────────────────────────────────────────────
  const loadAssembly = useCallback(async () => {
    if (!user?.assembly_id) { setLoading(false); return; }
    try {
      const r = await assemblyService.getById(user.assembly_id);
      setAssembly(r.data);
      setLoading(false);
    } catch { setLoading(false); }
  }, [user?.assembly_id]);

  // ── Cargar asistentes y preguntas ────────────────────────────────────────
  const loadDetails = useCallback(async () => {
    if (!user?.assembly_id) return;
    try {
      const [attRes, qRes] = await Promise.all([
        attendanceService.getAll({ assembly_id: user.assembly_id }),
        questionService.getAll({ assembly_id: user.assembly_id }),
      ]);
      setAttendances(attRes.data || []);
      setQuestions(Array.isArray(qRes.data) ? qRes.data : []);
    } catch {}
  }, [user?.assembly_id]);

  useEffect(() => {
    loadAssembly();
    loadDetails();
    intervalRef.current = setInterval(loadDetails, POLL_MS);
    return () => clearInterval(intervalRef.current);
  }, [loadAssembly, loadDetails]);

  // ── Cambiar estado de asamblea ───────────────────────────────────────────
  // Al FINALIZAR con votaciones abiertas, antes se pide confirmar (cierra con los presentes de ese momento).
  const [finalizar, setFinalizar]     = useState(null);   // { abiertas }
  const [finalizando, setFinalizando] = useState(false);
  const pedirCambioDeEstado = async (newStatus) => {
    if (newStatus === 'finalizada') {
      const abiertas = await votacionesAbiertas(assembly.id);
      if (abiertas.length) return setFinalizar({ abiertas });
    }
    return changeStatus(newStatus);
  };
  const changeStatus = async (newStatus) => {
    try {
      await assemblyService.updateStatus(assembly.id, newStatus);
      setAssembly(a => ({ ...a, status: newStatus }));
      setAlert({ type: 'success', message: `Asamblea ${STATUS_LABEL[newStatus].toLowerCase()}` });
      cerrarAlertaEn(3000);
    } catch (err) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Error al cambiar estado' });
      cerrarAlertaEn(6000);   // el mensaje del servidor puede ser más largo que el genérico
    }
  };

  // ── Abrir/cerrar votación de una pregunta ──────────────────────────────────────────
  // F-17 (b): abrir una votación sí/no pasa por la confirmación de su regla.
  const toggleQuestion = (q) => {
    if (!q.active) return setActivar(q);   // abrir: modal (regla sí/no + temporizador)
    return cambiarPregunta(q);
  };
  const cambiarPregunta = async (q) => {
    setToggling(q.id);
    try {
      if (q.active) await questionService.deactivate(q.id);
      else          await questionService.activate(q.id);
      if (q.active) quitarTemporizador(q.id);
      setAlert({ type: 'success', message: q.active ? 'Votación cerrada' : 'Votación abierta' });
      cerrarAlertaEn(3000);
      loadDetails();
      return true;
    } catch {
      setAlert({ type: 'danger', message: 'Error al cambiar estado de pregunta' });
      cerrarAlertaEn(3000);
      return false;
    } finally { setToggling(null); }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!user?.assembly_id || !assembly) return <NoAssemblyScreen />;

  // Stats
  const totalAttendees = attendances.length;
  const present        = attendances.filter(a => a.status === 'present').length;
  const pctPresent     = totalAttendees ? Math.round((present / totalAttendees) * 100) : 0;
  const activeQs       = questions.filter(q => q.active).length;
  const status         = assembly.status;

  return (
    <div className={styles.contenedor}>

      {alert && (
        <div className={`alert alert-${alert.type} ${styles.alertaClic}`} onClick={() => setAlert(null)}>
          {alert.message}
        </div>
      )}

      {/* ── Hero asamblea ── */}
      <div className={styles.hero}>
        <div className={styles.heroDecoracion} />

        <div className={styles.heroContenido}>
          <div className={styles.heroCabecera}>
            <div>
              <div className={styles.filaEstado}>
                <span className={styles.etiquetaEstado}
                  style={{ '--fondo': STATUS_COLOR[status] + '33', '--borde': `${STATUS_COLOR[status]}88` }}>
                  <i className={`fas fa-circle ${styles.puntoEstado}`} />
                  {STATUS_LABEL[status]}
                </span>
              </div>
              <h2 className={styles.heroTitulo}>
                {assembly.property?.name || `Asamblea #${assembly.number_assembly}`}
              </h2>
              <p className={styles.heroSub}>
                Asamblea #{assembly.number_assembly}
                {assembly.assembly_type ? ` · ${assembly.assembly_type}` : ''}
                {assembly.assembly_model ? ` · ${assembly.assembly_model}` : ''}
              </p>
            </div>

            {/* Controles de estado */}
            <div className={styles.controlesEstado}>
              {status === 'pendiente' && (
                <button onClick={() => changeStatus('en_progreso')}
                  className={`${styles.botonHero} ${styles.botonIniciar}`}>
                  <i className="fas fa-play" /> Iniciar asamblea
                </button>
              )}
              {status === 'en_progreso' && (
                <>
                  {['virtual','hybrid'].includes(assembly.assembly_model) && assembly.zoom_meeting_number && (
                    <button onClick={async () => {
                        try { await assemblyService.startMeeting(assembly.id); } catch (_) {}
                        abrirReunion({
                          assemblyId:   assembly.id,
                          assemblyName: `${assembly.property?.name || 'Asamblea'} #${assembly.number_assembly}`,
                          isModerator:  true,
                          // Staff: cerrar la ventana termina la reunión para todos.
                          alCerrar:     () => assemblyService.endMeeting(assembly.id),
                        });
                      }}
                      className={`${styles.botonHero} ${styles.botonVirtual}`}>
                      <i className={`fas fa-video ${styles.iconoVideo}`} /> Iniciar reunión virtual
                    </button>
                  )}
                  <button onClick={() => navigate(`/results/${assembly.id}`)}
                    className={`${styles.botonHero} ${styles.botonResultados}`}>
                    <i className="fas fa-chart-bar" /> Ver resultados
                  </button>
                  <button onClick={() => pedirCambioDeEstado('finalizada')}
                    className={`${styles.botonHero} ${styles.botonFinalizar}`}>
                    <i className="fas fa-stop" /> Finalizar asamblea
                  </button>
                </>
              )}
              {status === 'finalizada' && (
                <>
                  <button onClick={() => navigate(`/results/${assembly.id}`)}
                    className={`${styles.botonHero} ${styles.botonResultados}`}>
                    <i className="fas fa-chart-bar" /> Ver resultados finales
                  </button>
                  <AssemblyReport assemblyId={assembly.id} />
                </>
              )}
            </div>
          </div>

          {/* KPIs */}
          <div className={`kpi-grid-4 ${styles.kpiGrid}`}>
            {[
              { label: 'Quórum requerido', value: `${assembly.quorum}%`, icon: 'fa-percent' },
              { label: 'Asistentes',       value: `${present}/${totalAttendees}`, icon: 'fa-users' },
              { label: 'Cobertura',        value: `${pctPresent}%`, icon: 'fa-chart-pie' },
              { label: 'Votaciones abiertas',value: activeQs, icon: 'fa-circle-question' },
            ].map(k => (
              <div key={k.label} className={styles.kpi}>
                <div className={styles.kpiEtiqueta}>
                  <i className={`fas ${k.icon} ${styles.kpiIcono}`} />{k.label}
                </div>
                <div className={styles.kpiValor}>{k.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* F-17 (a): cuórum de ley sobre el total del conjunto + "Verificar cuórum" */}
      <CuorumPanel assembly={assembly} puedeVerificar />

      {finalizar && (
        <ConfirmarFinalizarModal abiertas={finalizar.abiertas} loading={finalizando}
          onClose={() => setFinalizar(null)}
          onConfirm={async () => { setFinalizando(true); await changeStatus('finalizada'); setFinalizando(false); setFinalizar(null); }} />
      )}

      {activar && (
        <ActivarPreguntaModal pregunta={activar} abriendo={toggling === activar.id}
          onClose={() => setActivar(null)}
          onCambiar={() => { setActivar(null); navigate('/admin/preguntas'); }}
          onAbrir={async (min) => { const ok = await cambiarPregunta(activar); if (ok) fijarTemporizador(activar.id, min); setActivar(null); }} />
      )}

      <div className="grid-2-col">

        {/* ── Panel asistencia ── */}
        <div className={`card ${styles.panel}`}>
          <div className={`card-header ${styles.panelCabecera}`}>
            <h2 className={styles.panelTitulo}>
              <i className={`fas fa-clipboard-list ${styles.iconoPanel}`} />
              Asistencia
            </h2>
            <button className="btn btn-sm btn-secondary"
              onClick={() => navigate('/admin/asistentes')}>
              <i className="fas fa-arrow-right" /> Gestionar
            </button>
          </div>

          {/* Barra quórum */}
          <div className={styles.barraBloque}>
            <div className={styles.barraFila}>
              <span>Presentes: <strong className={styles.valorFuerte}>{present}</strong></span>
              <span>Total: <strong className={styles.valorFuerte}>{totalAttendees}</strong></span>
            </div>
            <div className={styles.barraPista}>
              <div className={styles.barraRelleno} style={{
                '--tono': pctPresent >= assembly.quorum ? '#12b76a' : pctPresent >= assembly.quorum * 0.7 ? '#f79009' : '#f04438',
                '--ancho': `${Math.min(pctPresent, 100)}%`,
              }} />
            </div>
            <div className={styles.barraPie}>
              <span>{pctPresent}% de cobertura (personas)</span>
              <span>El cuórum de ley está arriba</span>
            </div>
          </div>

          {/* Lista últimos asistentes */}
          <div className={styles.listaAsistentes}>
            {attendances.length === 0 ? (
              <div className={styles.vacioAsistentes}>
                Sin asistentes registrados
              </div>
            ) : attendances.filter(a => a.status === 'present').slice(0, 8).map(a => (
              <div key={a.id} className={styles.filaAsistente}>
                <i className={`fas fa-circle-check ${styles.iconoPresente}`} />
                <span className={styles.nombreAsistente}>
                  {a.owner ? `${a.owner.nombre} ${a.owner.apellido}` : `Unidad ${a.unit?.numero_unidad || '—'}`}
                </span>
                <span className={styles.unidadAsistente}>
                  {a.unit?.numero_unidad || '—'} · {a.unit?.building?.numero_torre || '—'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Panel preguntas ── */}
        <div className={`card ${styles.panel}`}>
          <div className={`card-header ${styles.panelCabecera}`}>
            <h2 className={styles.panelTitulo}>
              <i className={`fas fa-circle-question ${styles.iconoPanel}`} />
              Preguntas de votación
            </h2>
            <button className="btn btn-sm btn-secondary"
              onClick={() => navigate('/admin/preguntas')}>
              <i className="fas fa-arrow-right" /> Gestionar
            </button>
          </div>

          <div className={styles.listaPreguntas}>
            {questions.length === 0 ? (
              <div className={styles.vacioPreguntas}>
                <i className={`fas fa-circle-question ${styles.iconoVacio}`} />
                Sin preguntas creadas
              </div>
            ) : questions.map(q => (
              <div key={q.id} className={styles.filaPregunta}>
                <div className={styles.textoPregunta}>
                  <div className={styles.preguntaTexto}>
                    {q.question_text}
                  </div>
                  <span className={`${styles.chipPregunta} ${q.active ? styles.chipAbierta : ''}`}>
                    {q.active ? '● Abierta' : '○ Cerrada'}
                  </span>
                  {q.active && <div className={styles.temporizador}><TemporizadorVotacion preguntaId={q.id} conAcciones /></div>}
                </div>
                <button
                  className={`btn btn-sm ${q.active ? 'btn-warning' : 'btn-success'} ${styles.botonPregunta} ${status !== 'en_progreso' ? styles.botonBloqueado : ''}`}
                  disabled={toggling === q.id || status !== 'en_progreso'}
                  title={status === 'finalizada' ? MOTIVO_FINALIZADA : status === 'pendiente' ? MOTIVO_NO_INICIADA : ''}
                  onClick={() => toggleQuestion(q)}>
                  {toggling === q.id
                    ? <i className="fas fa-spinner fa-spin" />
                    : <><i className={`fas ${q.active ? 'fa-pause' : 'fa-play'}`} /> {q.active ? 'Cerrar votación' : 'Abrir votación'}</>
                  }
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ticker */}
      <div className={styles.ticker}>
        <i className={`fas fa-sync ${styles.iconoTicker}`} />
        Actualizando cada {POLL_MS / 1000}s · {user?.name}
      </div>
    </div>
  );
}